"use client"

/**
 * Sitewide scrolling ticker bar — "maintenance at 3am, prepare for ~5 hours
 * downtime" style short urgent notices, distinct from both the patch-notes
 * modal (a full dialog the visitor has to dismiss) and the bespoke one-off
 * campaign popups (Early Access/Founder/GoonPals Beta — hand-built, used for
 * a handful of big moments, not this).
 *
 * Sourced from patch_notes.show_as_banner — that column and its admin
 * toggle ("Show as top banner" in the Releases → Patch Notes manager) have
 * existed since the table was extended for it, but nothing ever queried for
 * it or rendered a ticker. This is that missing piece, via the new
 * /api/patch-notes/latest-banner endpoint. A ticker note and a modal note
 * are independent — the same patch_notes table can have one active row for
 * each concern at once, they don't have to be the same content.
 *
 * Deliberately session-scoped dismissal (sessionStorage), not the permanent
 * per-account "seen" tracking the modal uses — a maintenance notice should
 * come back next session until the admin turns it off or it expires, not
 * disappear forever the first time anyone closes it.
 */

import { useEffect, useRef, useState } from "react"
import { X } from "@/lib/icons"
import { useTranslations } from "next-intl"

interface TickerNote {
  id: string
  version: string
  title: string
  announcement_type: "patch_note" | "maintenance" | "announcement" | "deal"
}

const DISMISSED_KEY_PREFIX = "goonru_ticker_dismissed_"

// Same visual language as the modal's own per-type distinction
// (components/patch-notes-modal.tsx's TYPE_META) — a maintenance notice
// reads as a warning, not the same color as a routine patch note.
const TYPE_STYLE: Record<TickerNote["announcement_type"], string> = {
  patch_note: "bg-primary text-primary-foreground",
  announcement: "bg-violet-600 text-white",
  maintenance: "bg-amber-500 text-black",
  deal: "bg-emerald-600 text-white",
}

function getDismissed(id: string): boolean {
  try {
    return sessionStorage.getItem(DISMISSED_KEY_PREFIX + id) === "1"
  } catch {
    return false
  }
}

function markDismissed(id: string) {
  try {
    sessionStorage.setItem(DISMISSED_KEY_PREFIX + id, "1")
  } catch {}
}

export function AnnouncementTicker() {
  const t = useTranslations("announcementTicker")
  const [ticker, setTicker] = useState<TickerNote | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const [animationDuration, setAnimationDuration] = useState(15)

  useEffect(() => {
    fetch("/api/patch-notes/latest-banner")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.ticker) {
          setTicker(d.ticker)
          setDismissed(getDismissed(d.ticker.id))
        }
      })
      .catch(() => {})
  }, [])

  // Same overflow-based measurement as TitleTicker (components/title-
  // ticker.tsx) — only actually scrolls if the text doesn't fit, so a short
  // message just sits still rather than jittering back and forth.
  useEffect(() => {
    if (!ticker || dismissed) return
    const el = containerRef.current
    const textEl = textRef.current
    if (!el || !textEl) return
    const containerWidth = el.offsetWidth
    const textWidth = textEl.scrollWidth
    if (textWidth > containerWidth) {
      setShouldAnimate(true)
      setAnimationDuration(Math.max(15, (textWidth / containerWidth) * 12))
    } else {
      setShouldAnimate(false)
    }
  }, [ticker, dismissed])

  if (!ticker || dismissed) return null

  const colorClass = TYPE_STYLE[ticker.announcement_type] ?? TYPE_STYLE.announcement

  return (
    <div
      className={`fixed top-0 inset-x-0 z-[9999] flex items-center gap-2 h-8 px-3 shadow-md ${colorClass}`}
    >
      <div ref={containerRef} className="flex-1 min-w-0 overflow-hidden">
        <div
          ref={textRef}
          className={`whitespace-nowrap text-xs font-semibold ${shouldAnimate ? "animate-scroll-title" : ""}`}
          style={shouldAnimate ? { animationDuration: `${animationDuration}s` } : undefined}
        >
          {ticker.title}
          {shouldAnimate && <span className="inline-block ml-16">{ticker.title}</span>}
        </div>
      </div>
      <button
        onClick={() => {
          markDismissed(ticker.id)
          setDismissed(true)
        }}
        className="shrink-0 h-5 w-5 rounded-full flex items-center justify-center bg-black/15 hover:bg-black/25 transition-colors"
        aria-label={t("dismiss")}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}
